import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Lock, Users, Clock } from "lucide-react";
import { Button } from "../ui/button";
import { fadeIn, fadeInUp, fadeInUpSmall } from "../../lib/animations";

export function FoundingMembersSection({ foundingStatus, navigate }) {
  if (!foundingStatus?.is_active) return null;

  return (
    <section className="relative z-10 px-6 md:px-12 lg:px-24 py-20 bg-white" data-testid="founding-members-section">
      <div className="max-w-3xl mx-auto">
        <motion.div
          initial={fadeInUpSmall.initial}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.05 }}
          className="bg-[#0A0A0F] rounded-2xl border border-white/10 p-8 md:p-10 shadow-xl"
        >
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-[#39FF14]/10 border border-[#39FF14]/30 text-[#39FF14] px-4 py-1.5 rounded-full text-xs font-semibold tracking-wider uppercase mb-5">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#39FF14] opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#39FF14]"></span>
              </span>
              Founding Members Program — Live
            </div>

            <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
              Lock in Your Price Before Launch
            </h2>
            <p className="text-gray-400 text-base max-w-xl mx-auto">
              Founding Members lock this price for life. Future pricing increases won't affect you. Once these spots are gone, they're gone.
            </p>
          </div>

          {/* Progress */}
          <div className="max-w-sm mx-auto mb-8">
            {(() => {
              const claimed = foundingStatus.spots_claimed || 0;
              const total = foundingStatus.total_spots || 50;
              const remaining = total - claimed;
              const pct = Math.max(2, (claimed / total) * 100);
              return (
                <>
                  <div className="flex items-center justify-between text-xs text-gray-400 mb-2 font-medium">
                    <span className="flex items-center gap-1.5">
                      <Users className="w-3.5 h-3.5 text-[#39FF14]" />
                      {claimed} spots claimed
                    </span>
                    <span>{remaining} remaining</span>
                  </div>
                  <div className="h-2 w-full bg-white/10 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: `${pct}%` }}
                      viewport={{ once: true, amount: 0.05 }}
                      transition={{ duration: 1, ease: "easeOut" }}
                      className="h-full bg-gradient-to-r from-[#39FF14] to-[#00D4FF] rounded-full"
                    />
                  </div>
                </>
              );
            })()}
          </div>

          {/* Benefits */}
          <div className="grid sm:grid-cols-3 gap-4 mb-8">
            {[
              { icon: Lock, text: "Price locked for life" },
              { icon: Users, text: "Priority feature access" },
              { icon: Clock, text: "Cancel anytime" },
            ].map(item => (
              <div key={item.text} className="flex items-center gap-2.5 p-3 bg-white/[0.05] rounded-lg border border-white/10">
                <item.icon className="w-4 h-4 text-[#39FF14] flex-shrink-0" />
                <span className="text-sm text-gray-300">{item.text}</span>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="text-center">
            <Button
              onClick={() => navigate('/pricing')}
              className="bg-gradient-to-r from-[#39FF14] to-[#00D4FF] text-black hover:opacity-90 font-semibold px-8 py-5 text-base h-auto shadow-lg shadow-[#39FF14]/20"
              data-testid="founding-member-cta"
            >
              Get Founding Member Access <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <p className="text-gray-600 text-xs mt-3">No long-term contracts. Cancel anytime.</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
